import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { IGetValueArgs, not } from "@components/smart/FieldInfo";
import { BackgroundFieldDef } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrSalaryAdvanceEntity,
    OtherLiabilityEntity,
    PrEmployeeEntity,
    PrSalaryAdvanceEntity
} from "@odata/GeneratedEntityTypes";
import { FiscalYearStatusCode } from "@odata/GeneratedEnums";
import { getFiscalYearByDate } from "@pages/fiscalYear/FiscalYear.utils";
import {
    createPeriodFromDate,
    getPayrollCurrencyFieldDef,
    getPayrollCurrencyTableFormatter
} from "@pages/payroll/payroll.utils";
import { MonthYearLocalPath } from "@pages/payroll/salaryAdvance/SalaryAdvance.utils";
import { isNewEntity } from "@utils/definition.utils";
import i18next from "i18next";
import { ValidationError } from "yup";

import { BasicInputSizes, FieldType, Sort, ValidatorType } from "../../../enums";
import { Model } from "../../../model/Model";
import { ROUTE_OTHER_LIABILITY } from "../../../routes";
import { getUtcDate, TDateLike } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { documentTableAmountFormatter } from "../../documents/Document.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getNumberOursSummaryDef, getNumberRangeFieldDefs } from "../../numberRange/NumberRange.utils";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import { getEmployeeFilterDef, getEmployeeSelectDef, getEmployeeTableColumnDefs } from "../employee/Employee.utils";
import SalaryAdvanceFormView, { ISalaryAdvanceFormStorageCustomData } from "./SalaryAdvanceFormView";


export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrSalaryAdvance}Table`,
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrSalaryAdvanceEntity.NumberOurs,
                PrSalaryAdvanceEntity.Employee,
            ],
            filterDefinition: {
                [PrSalaryAdvanceEntity.NumberOurs]: {},
                ...getEmployeeFilterDef(PrSalaryAdvanceEntity.Employee)
            },
            isValueHelp: true
        }],
        initialSortBy: [{ id: PrSalaryAdvanceEntity.NumberOurs, sort: Sort.Desc }],
        columns: [
            PrSalaryAdvanceEntity.NumberOurs,
            PrSalaryAdvanceEntity.Employee,
            createPath(PrSalaryAdvanceEntity.Employee, PrEmployeeEntity.NumberOurs),
            PrSalaryAdvanceEntity.Amount,
            PrSalaryAdvanceEntity.DateAccountingTransaction,
            PrSalaryAdvanceEntity.PairedDocument
        ],
        columnDefinition: {
            [PrSalaryAdvanceEntity.NumberOurs]: {},
            ...getEmployeeTableColumnDefs(PrSalaryAdvanceEntity.Employee),
            [PrSalaryAdvanceEntity.Amount]: {
                formatter: getPayrollCurrencyTableFormatter(PrSalaryAdvanceEntity.CurrencyCode)
            },
            [PrSalaryAdvanceEntity.DateAccountingTransaction]: {},
            [PrSalaryAdvanceEntity.PairedDocument]: {
                fieldSettings: {
                    displayName: OtherLiabilityEntity.NumberOurs
                }
            }
        },
        title: i18next.t("SalaryAdvances:Title"),
        additionalProperties: [
            { id: PrSalaryAdvanceEntity.Currency }
        ],
        // tabs needs to be present so that TableView renders them
        // but are filled dynamically inside SalaryAdvanceTableView
        tabs: [],
        tabsSettings: {}
    };
    const summary: ISummaryItem[] = [
        getNumberOursSummaryDef("SalaryAdvances")
    ];

    // enhance select def with SalaryCurrency and store it automatically into SalaryAdvance.Currency on selection
    const employeeSelectDefExtended = getEmployeeSelectDef();

    employeeSelectDefExtended.additionalProperties.push(
        {
            id: PrEmployeeEntity.SalaryCurrency
        }
    );
    employeeSelectDefExtended.fieldSettings.additionalProperties = [
        ...(employeeSelectDefExtended.fieldSettings.additionalProperties ?? []),
        { id: PrEmployeeEntity.SalaryCurrency }
    ];
    employeeSelectDefExtended.fieldSettings.localDependentFields.push(
        {
            from: { id: PrEmployeeEntity.SalaryCurrency },
            to: { id: PrSalaryAdvanceEntity.Currency }
        }
    );
    employeeSelectDefExtended.affectedFields = [
        ...(employeeSelectDefExtended.affectedFields ?? []),
        { id: PrSalaryAdvanceEntity.Amount }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.PrSalaryAdvance}Form`,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) =>
                getItemBreadCrumbsText(storage, i18next.t("SalaryAdvances:New"), i18next.t("SalaryAdvances:FormTitle")),
        isDeletable: true,
        formControl: SalaryAdvanceFormView,
        summary,
        fieldDefinition: {
            ...getNumberRangeFieldDefs("SalaryAdvances"),
            [PrSalaryAdvanceEntity.Employee]: { ...employeeSelectDefExtended },
            [PrSalaryAdvanceEntity.Amount]: {
                width: BasicInputSizes.S,
                ...getPayrollCurrencyFieldDef(PrSalaryAdvanceEntity.CurrencyCode),
                fieldSettings: {
                    ...getPayrollCurrencyFieldDef(PrSalaryAdvanceEntity.CurrencyCode).fieldSettings,
                    min: 0
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        excludeMin: true
                    }
                },
            },
            [MonthYearLocalPath]: {
                type: FieldType.MonthYear,
                label: i18next.t("SalaryAdvances:MonthYear"),
                isReadOnly: not(isNewEntity),
                isRequired: isNewEntity,
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: (value, args) => {
                            const storage = args.storage;
                            if (!isNewEntity({ storage })) {
                                return true;
                            }
                            const date = getUtcDate(value as TDateLike);
                            const { Year, Month } = createPeriodFromDate(date);
                            const period = (storage.getCustomData() as ISalaryAdvanceFormStorageCustomData).existingPeriods
                                .find(p => {
                                    return p.Year === Year && p.Month === Month;
                                });
                            if (!period) {
                                return new ValidationError(i18next.t("SalaryAdvances:Validation.InvalidPayrollPeriod"), value, args.bindingContext.getNavigationPath(true));
                            }
                            return true;
                        }
                    }
                }
            },
            [PrSalaryAdvanceEntity.Month]: {
                ...BackgroundFieldDef
            },
            [PrSalaryAdvanceEntity.Year]: {
                ...BackgroundFieldDef
            },
            [PrSalaryAdvanceEntity.DateAccountingTransaction]: {
                width: BasicInputSizes.M,
                defaultValue: getUtcDate(),
                validator: {
                    type: ValidatorType.Date,
                    settings: {
                        customValidator: (value, args) => {
                            const fiscalYear = getFiscalYearByDate(args.storage.context, value as Date);
                            if (fiscalYear?.StatusCode !== FiscalYearStatusCode.Active) {
                                return new ValidationError(i18next.t("SalaryAdvances:Validation.ActiveFY"), value, args.bindingContext.getNavigationPath(true));
                            }
                            return true;
                        }
                    }
                }
            }
        },
        additionalProperties: [
            { id: PrSalaryAdvanceEntity.PairedDocument },
            { id: PrSalaryAdvanceEntity.Currency }
        ],
        groups: [
            {
                id: "main",
                rows: [
                    [
                        { id: MonthYearLocalPath },
                        { id: PrSalaryAdvanceEntity.Employee },
                        { id: PrSalaryAdvanceEntity.Amount },
                        { id: PrSalaryAdvanceEntity.DateAccountingTransaction }
                    ]
                ]
            },
            {
                id: "Tabs",
                isVisible: not(isNewEntity),
                tabs: [
                    {
                        id: "PairedDocuments",
                        title: i18next.t("SalaryAdvances:FormTab.PairedDocuments"),
                        table: {
                            id: "pairedDocuments",
                            entitySet: EntitySetName.OtherLiabilities,
                            filter: (args: IGetValueArgs) => {
                                return `Id eq ${(args.storage.data.entity as IPrSalaryAdvanceEntity).PairedDocument?.Id}`;
                            },
                            initialSortBy: [
                                { id: "DateAccountingTransaction", sort: Sort.Asc }
                            ],
                            columns: [
                                { id: "DateAccountingTransaction" },
                                {
                                    id: "DocumentType",
                                    fieldSettings: {
                                        displayName: "Name"
                                    },
                                    label: i18next.t("Document:PairedDocuments.DocumentType")
                                },
                                {
                                    id: "NumberOurs",
                                    label: i18next.t("Document:PairedDocuments.Document"),
                                    formatter: (val, args) => {
                                        return getTableIntentLink(val as string, {
                                            route: `${ROUTE_OTHER_LIABILITY}/${(args.storage.data.entity as IPrSalaryAdvanceEntity).PairedDocument?.Id}`,
                                            context: args.storage.context,
                                            storage: args.storage
                                        });
                                    }
                                },
                                {
                                    id: "Amount",
                                    formatter: (val, args) => documentTableAmountFormatter(args.entity, args.context),
                                    additionalProperties: [
                                        { id: "TransactionAmount" }, { id: "TransactionCurrency" }
                                    ]
                                }
                            ]
                        }
                    }
                ]
            }
        ]
    };
    return {
        entitySet: EntitySetName.PrSalaryAdvances,
        table,
        form
    };
};
getDefinitions.translationFiles = ["SalaryAdvances", "Common", "NumberRange", "Document"];
setDefByEntityType(EntityTypeName.PrSalaryAdvance, getDefinitions);